import type { JSX } from 'react';

/** The id of the inline error under the field with this id, for `aria-describedby`. */
export function fieldErrorId(fieldId: string): string {
  return `${fieldId}-error`;
}

/** Shown when a save finds the prompt empty. */
export const PROMPT_REQUIRED = 'Enter the prompt.';

/** Shown when a save finds the name empty. */
export const NAME_REQUIRED = 'Enter a name.';

/** Shown when a save finds a run limit that is not a whole number of at least one. */
export const RUN_LIMIT_INVALID = 'Enter a whole number of runs, 1 or more.';

/** One field a save checks: its id, and why it is refused or null when it is fine. */
export interface FieldCheck {
  fieldId: string;
  message: string | null;
}

/** Move focus to the field a save refused, so the user lands on what to fix. */
export function focusField(fieldId: string): void {
  document.getElementById(fieldId)?.focus();
}

/** Focus the first refused field, in the order given; true when every field passed. */
export function focusFirstInvalid(checks: readonly FieldCheck[]): boolean {
  const firstInvalid = checks.find((check) => check.message !== null);
  if (firstInvalid === undefined) return true;
  focusField(firstInvalid.fieldId);
  return false;
}

/** The inline message under a field a save refused; nothing while there is no message. */
export function FieldError({
  fieldId,
  message,
}: {
  fieldId: string;
  message: string | null;
}): JSX.Element | null {
  if (message === null) return null;
  return (
    <p
      id={fieldErrorId(fieldId)}
      role="alert"
      className="text-caption text-destructive"
    >
      {message}
    </p>
  );
}
